import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Check if app is installed as PWA
export const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

// Register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[PWA] Service Worker registered:', registration.scope);
      return registration;
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission !== 'denied') {
    return await Notification.requestPermission();
  }
  return Notification.permission;
};

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Subscribe to push and send subscription to backend
export const subscribeToPush = async (registration, userId, token) => {
  if (!registration || !VAPID_PUBLIC_KEY) return null;
  
  try {
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }
    
    // Send subscription to backend
    if (subscription && userId) {
      const subJSON = subscription.toJSON();
      await fetch(`${API}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id: userId,
          subscription: {
            endpoint: subJSON.endpoint,
            keys: subJSON.keys,
          },
        }),
      });
      console.log('[PWA] Push subscription sent to backend');
    }
    
    return subscription;
  } catch (error) {
    console.error('[PWA] Push subscription failed:', error);
    return null;
  }
};

// Unsubscribe from push
export const unsubscribeFromPush = async (registration, userId, token) => {
  if (!registration) return;
  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      // Notify backend
      if (userId) {
        await fetch(`${API}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ user_id: userId, endpoint }),
        });
      }
      console.log('[PWA] Push unsubscribed');
    }
  } catch (error) {
    console.error('[PWA] Unsubscribe failed:', error);
  }
};

// Show local notification (fallback for when app is open)
export const showNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      badge: '/icons/badge-96x96.png',
      vibrate: [100, 50, 100],
      ...options
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.url) window.location.href = options.url;
    };
    return notification;
  }
  return null;
};

// Custom hook for PWA install prompt
export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(isPWA());
  
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    if (isPWA()) setIsInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  
  const promptInstall = async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setInstallPrompt(null);
      return true;
    }
    return false;
  };
  
  return { isInstallable, isInstalled, promptInstall };
};

// Custom hook for push notifications
export const usePushNotifications = () => {
  const [permission, setPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [swRegistration, setSwRegistration] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  
  useEffect(() => {
    registerServiceWorker().then(async (reg) => {
      setSwRegistration(reg);
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
      }
    });
  }, []);

  // Re-check permission when page gets focus (user might have changed browser settings)
  useEffect(() => {
    const checkPermission = () => {
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }
    };
    window.addEventListener('focus', checkPermission);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) checkPermission();
    });
    return () => {
      window.removeEventListener('focus', checkPermission);
      document.removeEventListener('visibilitychange', checkPermission);
    };
  }, []);
  
  const requestAndSubscribe = async (userId, token) => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'granted' && swRegistration) {
      const sub = await subscribeToPush(swRegistration, userId, token);
      setIsSubscribed(!!sub);
      return !!sub;
    }
    return false;
  };
  
  const unsubscribe = async (userId, token) => {
    await unsubscribeFromPush(swRegistration, userId, token);
    setIsSubscribed(false);
  };
  
  return {
    permission,
    isSubscribed,
    isSupported: 'Notification' in window && 'PushManager' in window,
    requestAndSubscribe,
    unsubscribe,
    showNotification,
  };
};

export default {
  isPWA,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  showNotification,
  usePWAInstall,
  usePushNotifications,
};
